/**
 * How each drum piece sounds: synthesized in Tone, one small voice per piece.
 *
 * There are no drum recordings to hand. The instrument samples come from the FluidR3 GM soundfont,
 * and that set is the 128 melodic patches with no percussion kit among them. So every piece here is
 * built out of Tone's own synths — a membrane for the skins, filtered noise for the scrapes and the
 * snare wires, a metal synth for the hats, two square waves for the bell.
 *
 * Kept apart from drumGrooves.js, which says what is played and when, so that this is the one file
 * a recorded piece would change. A recipe is a list of layers, and each layer is only a thing that
 * can be triggered and disposed — a Tone.Player holding a vendored one-shot fits that as well as a
 * synth does, and nothing that calls the kit would know the difference.
 *
 * Takes Tone as an argument rather than importing it, like toneVoices.js, so nothing drags the
 * library into a bundle that has not already loaded it.
 */

import {
  BONGO,
  BONGO_LOW,
  CLAVE,
  CONGA,
  COWBELL,
  CRASH,
  DRUM_PIECES,
  GUIRA,
  HAT,
  KICK,
  OPEN_HAT,
  RIDE,
  RIM,
  SHAKER,
  SLAP,
  SNARE,
  SWELL,
  TAMBOURINE,
  TIMBALE,
  TOM,
  TUMBA,
} from './drumGrooves';

/**
 * A struck skin: a sine that drops in pitch as it dies away, which is most of what a drum is.
 *
 * @param {object} options.note - Where the pitch settles, which is the drum's tuning
 * @param {number} options.volume - dB, the piece's place in the kit's own balance
 */
const membrane = ({ note, volume, duration = 0.1, pitchDecay = 0.008, octaves = 1.2, decay = 0.25 }) => (Tone, destination) => {
  const synth = new Tone.MembraneSynth({
    pitchDecay,
    octaves,
    envelope: { attack: 0.001, decay, sustain: 0, release: 0.05 },
  }).connect(destination);

  synth.volume.value = volume;

  return {
    trigger: (time, velocity) => synth.triggerAttackRelease(note, duration, time, velocity),
    dispose: () => synth.dispose(),
  };
};

/**
 * A burst of filtered noise — snare wires, a shaker, the güira's scrape.
 *
 * The only layer that reads `hold`: a scrape is as long as the hand draws it, so a groove says how
 * long, and a plain strike falls back to this layer's own length.
 */
const noise = ({ filter, type = 'highpass', volume, duration = 0.05, envelope, color = 'white' }) => (Tone, destination) => {
  const filterNode = new Tone.Filter(filter, type).connect(destination);
  const synth = new Tone.NoiseSynth({ noise: { type: color }, envelope }).connect(filterNode);

  synth.volume.value = volume;

  return {
    trigger: (time, velocity, hold) => synth.triggerAttackRelease(hold || duration, time, velocity),
    dispose: () => {
      synth.dispose();
      filterNode.dispose();
    },
  };
};

/**
 * Noise rising across `hold` through a filter that opens with it, stopping dead on its peak — a
 * cymbal played backwards. The attack is set per strike, since it is the whole length of the hit.
 */
const swell = ({ from, to, volume }) => (Tone, destination) => {
  const filterNode = new Tone.Filter(from, 'lowpass').connect(destination);
  const synth = new Tone.NoiseSynth({
    noise: { type: 'white' },
    envelope: { attack: 1, attackCurve: 'exponential', decay: 0.01, sustain: 1, release: 0.03 },
  }).connect(filterNode);

  synth.volume.value = volume;

  return {
    trigger: (time, velocity, hold) => {
      const length = hold || 1;

      synth.envelope.attack = length;
      filterNode.frequency.cancelScheduledValues(time);
      filterNode.frequency.setValueAtTime(from, time);
      filterNode.frequency.exponentialRampToValueAtTime(to, time + length);
      synth.triggerAttackRelease(length, time, velocity);
    },
    dispose: () => {
      synth.dispose();
      filterNode.dispose();
    },
  };
};

/**
 * Inharmonic metal — the hi-hat, closed or open depending only on how long it rings, or a ride. A
 * `hold` rings it that long instead, as a crash let ring out.
 */
const metal = ({ volume, decay, frequency = 300 }) => (Tone, destination) => {
  const synth = new Tone.MetalSynth({
    envelope: { attack: 0.001, decay, release: 0.01 },
    harmonicity: 5.1,
    modulationIndex: 32,
    resonance: 4000,
    octaves: 1.5,
  }).connect(destination);

  synth.volume.value = volume;

  return {
    trigger: (time, velocity, hold) => {
      synth.envelope.decay = hold || decay;
      synth.triggerAttackRelease(frequency, 0.02, time, velocity);
    },
    dispose: () => synth.dispose(),
  };
};

/**
 * A pitched knock with no pitch drop — a clave, or one of the two tones of a bell. Optionally through
 * a bandpass, which is what turns two square waves into a cowbell rather than a chord.
 */
const knock = ({ frequency, wave, volume, decay, bandpass = null }) => (Tone, destination) => {
  const filterNode = bandpass ? new Tone.Filter({ frequency: bandpass, type: 'bandpass', Q: 1.5 }).connect(destination) : null;
  const synth = new Tone.Synth({
    oscillator: { type: wave },
    envelope: { attack: 0.001, decay, sustain: 0, release: 0.02 },
  }).connect(filterNode || destination);

  synth.volume.value = volume;

  return {
    trigger: (time, velocity) => synth.triggerAttackRelease(frequency, 0.05, time, velocity),
    dispose: () => {
      synth.dispose();
      if (filterNode) filterNode.dispose();
    },
  };
};

/**
 * Every piece's layers. Levels are set against each other by ear, with the kick as the reference; the
 * whole kit is then brought up or down against the chords by the drums slider.
 */
export const DRUM_RECIPES = {
  [KICK]: [
    membrane({ note: 'C1', volume: -2, duration: 0.3, pitchDecay: 0.05, octaves: 6, decay: 0.4 }),
    // The beater's click, so the kick is heard on small speakers and not only felt
    noise({ filter: 3000, type: 'bandpass', volume: -20, duration: 0.01, envelope: { attack: 0.001, decay: 0.015, sustain: 0, release: 0.01 } }),
  ],
  [SNARE]: [
    noise({ filter: 1500, volume: -10, duration: 0.12, envelope: { attack: 0.001, decay: 0.18, sustain: 0, release: 0.06 } }),
    // The shell's body under the wires
    membrane({ note: 'E3', volume: -9, pitchDecay: 0.02, octaves: 2, decay: 0.14 }),
  ],
  [RIM]: [membrane({ note: 'C5', volume: -10, duration: 0.03, pitchDecay: 0.005, octaves: 2, decay: 0.05 })],
  [HAT]: [metal({ volume: -26, decay: 0.05 })],
  [OPEN_HAT]: [metal({ volume: -28, decay: 0.35 })],
  [SHAKER]: [
    noise({ filter: 6000, volume: -16, duration: 0.03, envelope: { attack: 0.01, decay: 0.05, sustain: 0, release: 0.02 } }),
  ],
  [GUIRA]: [
    noise({
      filter: 5500,
      type: 'bandpass',
      volume: -10,
      duration: 0.03,
      envelope: { attack: 0.005, decay: 0.04, sustain: 0.25, release: 0.04 },
    }),
  ],
  [CONGA]: [membrane({ note: 'C4', volume: -6 })],
  [SLAP]: [
    membrane({ note: 'G4', volume: -8, duration: 0.04, decay: 0.06 }),
    noise({ filter: 3000, type: 'bandpass', volume: -12, duration: 0.02, envelope: { attack: 0.001, decay: 0.04, sustain: 0, release: 0.01 } }),
  ],
  [TUMBA]: [membrane({ note: 'F#3', volume: -5, decay: 0.3 })],
  [BONGO]: [membrane({ note: 'G#4', volume: -9, decay: 0.12 })],
  [BONGO_LOW]: [membrane({ note: 'D#4', volume: -8, decay: 0.18 })],
  [COWBELL]: [
    knock({ frequency: 540, wave: 'square', volume: -22, decay: 0.25, bandpass: 800 }),
    knock({ frequency: 800, wave: 'square', volume: -22, decay: 0.25, bandpass: 800 }),
  ],
  // A high, short skin with a little of the steel shell ringing over it
  [TIMBALE]: [
    membrane({ note: 'E5', volume: -8, duration: 0.05, pitchDecay: 0.004, octaves: 1.5, decay: 0.12 }),
    metal({ volume: -32, decay: 0.12, frequency: 900 }),
  ],
  [CLAVE]: [knock({ frequency: 2500, wave: 'sine', volume: -8, decay: 0.05 })],
  [TOM]: [membrane({ note: 'F2', volume: -6, duration: 0.3, pitchDecay: 0.03, octaves: 2, decay: 0.45 })],
  // Lower and longer than the hat, and quieter, so it washes rather than ticks
  [RIDE]: [metal({ volume: -32, decay: 0.8, frequency: 450 })],
  // Jingles: a bright burst with a little metal ring over it
  [TAMBOURINE]: [
    noise({ filter: 7000, volume: -14, duration: 0.06, envelope: { attack: 0.002, decay: 0.12, sustain: 0, release: 0.05 } }),
    metal({ volume: -34, decay: 0.1, frequency: 800 }),
  ],
  // A long metal ring under a wash of noise, the two dying away together
  [CRASH]: [
    metal({ volume: -30, decay: 1.5, frequency: 350 }),
    noise({ filter: 5000, volume: -20, duration: 0.05, envelope: { attack: 0.002, decay: 1.2, sustain: 0, release: 0.3 } }),
  ],
  [SWELL]: [swell({ from: 800, to: 12000, volume: -14 })],
};

/**
 * A kit holding voices for just the pieces a groove uses.
 *
 * Levels go through as the groove writes them, not through curveVelocity the way the chords' do:
 * that curve lifts a recorded clip's middling velocities, and a groove's soft hat is soft on purpose.
 *
 * @param {object} Tone - The loaded Tone module
 * @param {object} destination - What every piece plays into — the drums' own gain
 * @param {string[]} pieces - The pieces to build; one missing from DRUM_RECIPES is skipped
 * @returns {{ trigger: function(piece, time, velocity, hold), dispose: function }} `hold` in seconds
 */
export const createDrumKit = (Tone, destination, pieces = DRUM_PIECES) => {
  const voices = new Map();

  new Set(pieces).forEach((piece) => {
    const recipe = DRUM_RECIPES[piece];

    if (recipe) voices.set(piece, recipe.map((layer) => layer(Tone, destination)));
  });

  return {
    trigger: (piece, time, velocity = 1, hold = null) => {
      const layers = voices.get(piece);

      if (!layers) return;

      layers.forEach((layer) => {
        // A drum hit that throws would otherwise take down every event the transport had left to
        // fire at that tick — the chord struck on the same beat included. Losing one hit is the
        // better failure.
        try {
          layer.trigger(time, velocity, hold);
        } catch (error) {
          console.warn(`Drum hit on ${piece} skipped`, error);
        }
      });
    },
    dispose: () => {
      voices.forEach((layers) => layers.forEach((layer) => layer.dispose()));
      voices.clear();
    },
  };
};
